import { AxiosResponse } from 'axios';
import { ProductMapping } from './ProductMapping';
import { VerificationResponse } from './VerificationResponse';
import VersionedResource from '../VersionedResource';

export default class ProductMappingResource extends VersionedResource {
  public async get(fiId: number | string): Promise<ProductMapping[]> {
    const url = `/api/rcx/config/${fiId}/product-mapping`;

    try {
      const response: AxiosResponse = await this.apiClient.get(url);
      return response.data.data;
    } catch (error) {
      // Will 404 if ProductMapping has not been set up yet, so return empty ProductMapping
      if (error.response.status !== 404) {
        throw error;
      } else {
        return ProductMappingResource.createEmptyProductMapping();
      }
    }
  }

  public async save(fiId: string, mapping: ProductMapping): Promise<ProductMapping> {
    const url = `/api/rcx/config/${fiId}/product-mapping`;
    let response: AxiosResponse;

    if (mapping.id) {
      response = await this.apiClient.put(`${url}/${mapping.id}`, mapping);
    } else {
      response = await this.apiClient.post(url, mapping);
    }

    const savedMapping: ProductMapping = response.data.data;
    return savedMapping;
  }

  public async remove(fiId: number, productMappingId: number): Promise<AxiosResponse> {
    return this.apiClient.delete(`/api/rcx/config/${fiId}/product-mapping/${productMappingId}`);
  }

  public async mergeMappings(fiId: number, mappingIds: number[]): Promise<AxiosResponse> {
    return this.apiClient.put(`/api/rcx/config/${fiId}/product-mapping/`, mappingIds);
  }

  public async export(fiId: number): Promise<Blob> {
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/product-mapping/export`, { responseType: 'blob' },
    );
    return response.data;
  }

  public async downloadErrorReport(fiId: number): Promise<Blob> {
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/product-mapping/error-report`, { responseType: 'blob' },
    );
    return response.data;
  }

  public async upload(fiId: number, file: File): Promise<VerificationResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response: AxiosResponse = await this.apiClient.post(`/api/rcx/config/${fiId}/product-mapping/upload-productmaps`,
      formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

    return response.data.data;
  }

  public async verify(fiId: number): Promise<VerificationResponse> {
    const response: AxiosResponse = await this.apiClient.get(`/api/rcx/config/${fiId}/product-mapping/verify`);
    return response.data.data;
  }

  public static createEmptyProductMapping(): ProductMapping[] {
    return [{
      id: null!,
      coreAccountProductTypes: [],
      accountType: null!,
      productType: null!,
      kasasaBranded: false,
      kasasaPowered: false,
      fiProductName: null!,
      hasAssociatedAccounts: false,
      externalProductMappingId: null!,
      productSubType: null!,
      productAcquisitionSource: null!,
      productClassification: null!,
    }];
  }
}
